import {KeyboardEvent, useMemo, useState} from 'react';
import styled, {DefaultTheme} from 'styled-components';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router';
import {useQuery} from 'react-query';
import {useStoreContext} from '@store/store-context';
import {ItemLoader} from '@ui/organisms/item-loader';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {
  attachCycleEnrichment,
  buildOccurrenceGroups,
  getAdminSubmissionFilter,
  getCycleDestination,
  getEffectiveCycleStatus,
  getNextRunDate,
  getParticipationStats,
  getSubmissionRows,
  getSubmissionCycleId,
  ManagedCycle,
  withoutPendingInvitationSubmissions,
} from './team-management.utils';

const StatusTabs = styled.div`
  display: flex;
  width: fit-content;
  min-width: 0;
  align-items: center;
  gap: 4px;
  margin-bottom: 24px;
  padding: 4px;
  border-radius: 10px;
  background: ${({theme}) => theme.colors.backgroundDark};

  @media (max-width: 640px) {
    width: 100%;

    > button {
      flex: 1;
      min-width: 0;
      padding-right: 8px;
      padding-left: 8px;
    }
  }
`;

const StatusTab = styled.button<{$active: boolean}>`
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: 0;
  border-radius: 8px;
  appearance: none;
  background: ${({$active, theme}) =>
    $active ? theme.colors.white : 'transparent'};
  box-shadow: ${({$active}) =>
    $active ? '0 2px 8px rgba(19, 17, 17, 0.05)' : 'none'};
  color: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.textBody};
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  cursor: pointer;
  transition: color 160ms ease;

  @media (hover: hover) {
    &:hover {
      color: ${({theme}) => theme.colors.purple300};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.purple300};
    outline-offset: 2px;
  }
`;

const BodyCard = styled.section`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  background: ${({theme}) => theme.colors.white};
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 17px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 1;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchField = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 46px;
  padding: 12px 44px 12px 16px;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  outline: none;
  font-size: 14px;
  line-height: 22px;
  &::placeholder {
    color: ${({theme}) => theme.colors.textBody};
  }
  &:focus {
    border-color: ${({theme}) => theme.colors.purple300};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({theme}) => theme.colors.textBody};
`;

type TableColumn =
  | 'cycleName'
  | 'status'
  | 'lastSent'
  | 'participation'
  | 'visibility'
  | 'action';

const columnAlignments: Record<TableColumn, 'left' | 'center'> = {
  cycleName: 'left',
  status: 'center',
  lastSent: 'left',
  participation: 'center',
  visibility: 'center',
  action: 'center',
};

const TableGrid = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-template-columns:
    minmax(0, 203fr) minmax(0, 180fr) minmax(0, 203fr)
    minmax(0, 166fr) minmax(0, 166fr) minmax(0, 168fr);
  align-items: center;
  gap: 0;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const TableHeaderRow = styled(TableGrid)`
  min-height: 48px;
  background: ${({theme}) => theme.colors.backgroundLight};
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px 10px 0 0;

  > span {
    min-width: 0;
    padding: 0 16px;
    color: ${({theme}) => theme.colors.textBody};
    font: 500 13px/18px Graphik, sans-serif;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled(TableGrid).attrs({as: 'div'})`
  background: ${({theme}) => theme.colors.white};
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  margin-top: -1px;
  min-height: 76px;
  cursor: pointer;
  &:last-child {
    border-radius: 0 0 10px 10px;
  }
  &:hover,
  &:focus-visible {
    background: ${({theme}) => theme.colors.backgroundLight};
  }
  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.purple300};
    outline-offset: -2px;
  }
  > div {
    padding: 16px;
  }

  @media (max-width: 768px) {
    align-items: stretch;
    min-height: 0;
    margin-top: 8px;
    padding: 8px 0 12px;
    border-radius: 10px;
    position: relative;
  }
`;

const TableSurface = styled.div`
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    ${TableHeaderRow} + ${TableRow} {
      margin-top: 0;
    }
  }
`;

const HeaderCell = styled.span<{$column: TableColumn}>`
  text-align: ${({$column}) => columnAlignments[$column]};
`;

const Cell = styled.div<{$column: TableColumn}>`
  min-width: 0;
  overflow: hidden;
  text-align: ${({$column}) => columnAlignments[$column]};
`;

const NameCell = styled(Cell)`
  @media (max-width: 768px) {
    padding-right: 56px !important;
  }
`;

const DetailCell = styled(Cell)`
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    text-align: left;

    > * {
      justify-self: start;
    }

    &::before {
      content: attr(data-label);
      color: ${({theme}) => theme.colors.textBody};
      font-size: 12px;
      line-height: 20px;
      font-weight: 400;
    }
  }

  @media (max-width: 360px) {
    grid-template-columns: 88px minmax(0, 1fr);
  }
`;

const ActionCell = styled(Cell)`
  @media (max-width: 768px) {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 0 !important;
  }
`;

const CycleNameText = styled.div`
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${({theme}) => theme.colors.blackTwo};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const NextRunCaption = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${({theme}) => theme.colors.textBody};
`;

type DotTone = 'success' | 'warning' | 'muted';

const dotToneColor = (tone: DotTone, theme: DefaultTheme) => {
  if (tone === 'success') return theme.colors.green300;
  if (tone === 'warning') return theme.colors.orange300;
  return theme.colors.textMuted;
};

const StatusCell = styled.div<{$tone: DotTone}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.blackTwo};
  text-transform: capitalize;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({$tone, theme}) => dotToneColor($tone, theme)};
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const LastSentText = styled.div`
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${({theme}) => theme.colors.blackTwo};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const ParticipationCell = styled.div<{$tone: DotTone}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({$tone, theme}) => dotToneColor($tone, theme)};
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const ParticipationCount = styled.span`
  color: ${({theme}) => theme.colors.blackTwo};
`;

const ParticipationPercent = styled.span`
  color: ${({theme}) => theme.colors.textBody};
`;

const MutedParticipation = styled.div`
  color: ${({theme}) => theme.colors.textMuted};
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
`;

const VisibilityPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${({theme}) => theme.colors.backgroundDark};
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.textBody};
  text-transform: capitalize;
`;

const Chevron = styled.button`
  width: 24px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${({theme}) => theme.colors.textBody};
  font-size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${({theme}) => theme.colors.borderLight};
  }
`;

const Empty = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${({theme}) => theme.colors.textBody};
`;

interface CycleListTableProps {
  cycles: ManagedCycle[];
  isLoading: boolean;
}

const statusDotTone = (status: string): DotTone => {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  return 'muted';
};

const participationDotTone = (
  percentage: number,
  submittedCount: number,
): DotTone => {
  if (submittedCount === 0) return 'muted';
  return percentage >= 70 ? 'success' : 'warning';
};

const SearchGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M7.33317 12.6667C10.2787 12.6667 12.6665 10.2789 12.6665 7.33333C12.6665 4.38781 10.2787 2 7.33317 2C4.38765 2 1.99984 4.38781 1.99984 7.33333C1.99984 10.2789 4.38765 12.6667 7.33317 12.6667Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 14L11.1003 11.1003"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CycleListTable = ({cycles, isLoading}: CycleListTableProps) => {
  const navigate = useNavigate();
  const {
    authStore: {auth},
    usersStore: {allUsers},
  } = useStoreContext();
  const [view, setView] = useState<'active' | 'inactive'>('active');
  const [search, setSearch] = useState('');
  const request = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const controller = useMemo(() => new CompanyCheckinsController(request), [
    request,
  ]);

  const workspaceId = auth.user.workspace.id;
  const baseFilter = getAdminSubmissionFilter();
  const {data: participantResponse, isLoading: loadingParticipants} = useQuery(
    ['cycle-submissions', workspaceId, 'workspace', 'user'],
    () => controller.getListOfCheckins({...baseFilter, include: 'user'}),
    {enabled: Boolean(workspaceId), refetchOnWindowFocus: false},
  );
  const {data: cycleResponse, isLoading: loadingCycles} = useQuery(
    ['cycle-submissions', workspaceId, 'workspace', 'cycle'],
    () => controller.getListOfCheckins({...baseFilter, include: 'cycle'}),
    {enabled: Boolean(workspaceId), refetchOnWindowFocus: false},
  );

  const submissions = useMemo(
    () =>
      withoutPendingInvitationSubmissions(
        attachCycleEnrichment(
          getSubmissionRows(participantResponse),
          getSubmissionRows(cycleResponse),
        ),
        allUsers,
      ),
    [allUsers, participantResponse, cycleResponse],
  );

  const availableCycles = useMemo(() => {
    const enrichedById = new Map<string, ManagedCycle>();
    submissions.forEach((row) => {
      if (typeof row.cycle === 'object')
        enrichedById.set(row.cycle.id, row.cycle);
    });
    return cycles.map((cycle) => ({...enrichedById.get(cycle.id), ...cycle}));
  }, [cycles, submissions]);

  const filteredCycles = useMemo(() => {
    const term = search.trim().toLowerCase();
    return availableCycles
      .filter(
        (cycle) =>
          (view === 'active') === (getEffectiveCycleStatus(cycle) === 'active'),
      )
      .filter((cycle) => !term || cycle.cycleName.toLowerCase().includes(term))
      .sort(
        (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
      );
  }, [availableCycles, search, view]);

  const openCycle = (
    cycle: ManagedCycle,
    tab: 'responses' | 'settings' = 'responses',
  ) => navigate(getCycleDestination(cycle, tab));
  const onRowKeyDown = (event: KeyboardEvent, cycle: ManagedCycle) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCycle(cycle);
    }
  };

  const loading = isLoading || loadingParticipants || loadingCycles;

  return (
    <>
      <StatusTabs role="tablist" aria-label="Cycle status">
        <StatusTab
          type="button"
          role="tab"
          aria-selected={view === 'active'}
          $active={view === 'active'}
          onClick={() => setView('active')}>
          Active cycles
        </StatusTab>
        <StatusTab
          type="button"
          role="tab"
          aria-selected={view === 'inactive'}
          $active={view === 'inactive'}
          onClick={() => setView('inactive')}>
          Inactive cycles
        </StatusTab>
      </StatusTabs>
      <BodyCard>
        <Toolbar>
          <SearchField>
            <SearchInput
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <SearchIcon aria-hidden="true">
              <SearchGlyph />
            </SearchIcon>
          </SearchField>
        </Toolbar>

        <TableSurface>
          <TableHeaderRow data-testid="cycle-table-header">
            <HeaderCell $column="cycleName">Cycle Name</HeaderCell>
            <HeaderCell $column="status">Status</HeaderCell>
            <HeaderCell $column="lastSent">Last Sent</HeaderCell>
            <HeaderCell $column="participation">Participation</HeaderCell>
            <HeaderCell $column="visibility">Visibility</HeaderCell>
            <HeaderCell $column="action" />
          </TableHeaderRow>

          {loading ? (
            <Empty>
              <ItemLoader />
            </Empty>
          ) : filteredCycles.length ? (
            filteredCycles.map((cycle) => {
              const cycleSubmissions = submissions.filter(
                (row) => getSubmissionCycleId(row) === cycle.id,
              );
              const occurrences = buildOccurrenceGroups(
                cycleSubmissions,
                cycle,
                auth.user.workspace?.timezone,
              );
              const latest = occurrences[0];
              const stats = getParticipationStats(latest?.submissions || []);
              const nextRun = getNextRunDate(
                cycle,
                new Date(),
                occurrences.length,
              );
              const status = getEffectiveCycleStatus(cycle);
              const hasSent = Boolean(latest);
              const dotTone = participationDotTone(
                stats.percentage,
                stats.submittedCount,
              );
              return (
                <TableRow
                  key={cycle.id}
                  data-testid={`cycle-table-row-${cycle.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openCycle(cycle)}
                  onKeyDown={(event) => onRowKeyDown(event, cycle)}>
                  <NameCell
                    $column="cycleName"
                    data-testid={`cycle-table-cell-${cycle.id}-cycleName`}>
                    <CycleNameText title={cycle.cycleName}>
                      {cycle.cycleName}
                    </CycleNameText>
                    {nextRun && (
                      <NextRunCaption>
                        Next: {dayjs(nextRun).format('MMM D, YYYY')}
                      </NextRunCaption>
                    )}
                  </NameCell>
                  <DetailCell
                    $column="status"
                    data-label="Status"
                    data-testid={`cycle-table-cell-${cycle.id}-status`}>
                    <StatusCell $tone={statusDotTone(status)}>
                      {status || 'Unknown'}
                    </StatusCell>
                  </DetailCell>
                  <DetailCell
                    $column="lastSent"
                    data-label="Last Sent"
                    data-testid={`cycle-table-cell-${cycle.id}-lastSent`}>
                    <LastSentText>
                      {hasSent ? dayjs(latest.key).format('MMM D, YYYY') : '–'}
                    </LastSentText>
                  </DetailCell>
                  <DetailCell
                    $column="participation"
                    data-label="Participation"
                    data-testid={`cycle-table-cell-${cycle.id}-participation`}>
                    {stats.totalCount ? (
                      <ParticipationCell $tone={dotTone}>
                        <ParticipationCount>
                          {stats.submittedCount}/{stats.totalCount}
                        </ParticipationCount>
                        <ParticipationPercent>
                          ({stats.percentage}%)
                        </ParticipationPercent>
                      </ParticipationCell>
                    ) : (
                      <ParticipationCell $tone="muted">
                        <MutedParticipation>Not launched</MutedParticipation>
                      </ParticipationCell>
                    )}
                  </DetailCell>
                  <DetailCell
                    $column="visibility"
                    data-label="Visibility"
                    data-testid={`cycle-table-cell-${cycle.id}-visibility`}>
                    <VisibilityPill>
                      {cycle.visibility || 'Unknown'}
                    </VisibilityPill>
                  </DetailCell>
                  <ActionCell
                    $column="action"
                    data-testid={`cycle-table-cell-${cycle.id}-action`}>
                    <Chevron
                      aria-label={
                        status === 'draft'
                          ? `Continue editing ${cycle.cycleName}`
                          : `Open ${cycle.cycleName} settings`
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        openCycle(cycle, 'settings');
                      }}>
                      ›
                    </Chevron>
                  </ActionCell>
                </TableRow>
              );
            })
          ) : (
            <Empty>No {view} cycles match your search.</Empty>
          )}
        </TableSurface>
      </BodyCard>
    </>
  );
};
